import React, { memo, useEffect, useRef } from 'react';
import { NodeSize } from '@/types';
import type { Editor } from '@tiptap/react';
import { RichTextInput } from '../RichTextInput';
import { TextNodeResizeHandle } from '../TextNodeResizeHandle';
import { useCtrlWheelZoom } from '../../hooks';

export const SelfWriteEditView: React.FC<{
  nodeId: string;
  editor: Editor | null;
  onEnterFullscreen: () => void;
  onExitEdit: () => void;
  onResize: (size: NodeSize) => void;
}> = memo(({ nodeId, editor, onEnterFullscreen, onExitEdit, onResize }) => {
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCtrlWheel = useCtrlWheelZoom();

  // 点击节点外部（画布空白）时退出编辑
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // 点击在编辑器区域内 → 不退出
      const editorArea = document.querySelector(`[data-editor-id="${nodeId}"]`);
      if (editorArea?.contains(target)) return;
      // 点击在浮动工具栏内 → 不退出
      if (target.closest('.floating-toolbar')) return;
      // 点击在 resize 手柄内 → 不退出
      if (target.closest('[data-node-card]')) return;
      // 点击在节点外部 → 退出编辑
      onExitEdit();
    };

    // 延迟绑定，避免双击进入编辑时立即触发退出
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleMouseDown, true);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [nodeId, onExitEdit]);

  return (
    <div className="relative w-full h-full" data-id={nodeId}>
      <div className="overflow-y-auto rich-text-scroll nodrag nowheel" onWheel={handleCtrlWheel} style={{ width: 'calc(100% - 27px)', height: 'calc(100% - 40px)', transform: 'translate(18px, 20px)' }} data-editor-id={nodeId}>
        <div className="w-full h-full flex justify-center items-center pr-3">
          <RichTextInput
            editor={editor}
            className="h-full"
          />
        </div>
      </div>
      <TextNodeResizeHandle onResize={onResize} />
    </div>
  );
});
